import { connect } from 'react-redux';
import { generateImageURL } from '@reducers/blobStorage';
import { getConnectorInfo } from '@reducers/connector';
import { setGlobalSelectedLevel } from '@reducers/globalAdd';
import { getMachines, setSelectedMachine, setSelectedTime, updateMachineInfo } from '@reducers/machines';
import { getTimeSetup } from '@reducers/time';
import ColorWrapper from '@styles/colors';
import { MachineMainWrapperProps } from '@v2/pages/machines/common/types/props';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fullHeight, scale } from '@components/frame';
import { ScrollView, View } from 'react-native';
import { useFocusEffect, useRoute } from '@react-navigation/native';
import { isPhone, isWeb, localTime } from '@helpers';
import { MODE } from '@v2/pages/machines/common/enums';
import IdleTimerManager from 'react-native-idle-timer';
import MissingPIModal from '@components/machines/components/modals/missingPIModal';
import MachineActionsModal from '@components/machines/components/modals/machineActionsModal';
import MachinesSelectModal from '@components/machines/components/modals/machinesSelectModal';
import { MachinesContext, MachinesContextProps } from './MachinesContext';
import MachinesScreenContentWrapper from '@v2/pages/machines/common/wrappers/MachinesScreenContentWrapper/MachinesScreenContentWrapper';
import { useHandleNavigationMachinesHeader } from '@v2/pages/machines/hooks/useHandleNavigationMachineHeader';
import {
  setFirstRenderMachinesProvider,
  setInitialEventDate,
  setInitialLoading,
  setSelectedChartDate,
  setSelectedChartDateEnd,
} from '../../../../../../../reducers/machinesV2';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { useHandleMachinesScreenTitle } from '@v2/pages/machines/hooks/useHandleMachinesScreenTitle';
import { setTimePeriod } from '@reducers/params';
import { ALL_MACHINES_OBJECT, LIST_WIDTH } from '@v2/pages/common/constants/base.constants';
import { appVisibilityService } from '@services/appVisibility';

let interval: number | null = null;
let initialLoadingGlobal = true;

const MachinesProvider: FC<MachineMainWrapperProps> = ({
  children,
  getMachines,
  setSelectedMachine,
  updateMachineInfo,
  machines: { machines, machinesLoaded, selectedItem, machineStatistics },
  machines: machinesStorage,
  C,
  time,
  mode,
  panelViewMode,
  onBack,
  lg,
  generateImageURL,
  getTimeSetup,
  getConnectorInfo,
  machineListOEE,
  getList,
  withDefaultComponent,
  withoutHeader,
  isMachinesSelectModalOpen,
  actionsModalOpen,
  setInitialLoading,
  setInitialEventDate,
  firstRenderMachinesProvider,
  setFirstRenderMachinesProvider,
  setSelectedChartDate,
  setSelectedChartDateEnd,
  setTimePeriod,
}) => {
  const s = scale;

  const [isFocused, setIsFocused] = useState(false);

  const globalScroll = useRef<ScrollView | null>(null);
  const route = useRoute();

  useHandleMachinesScreenTitle();

  const {
    isMachinesScreen,
    shouldWorkWithDateTime,
  } = useHandleNavigationMachinesHeader();
  const { isAllSelected, id } = useHandleParams();

  const currentMode = useMemo(() => {
    if (mode) return mode;
    if (isPhone && isAllSelected) return MODE.LIST;
    if (isPhone && !isAllSelected) return MODE.DETAILS;
    return MODE.FULL;
  }, [mode, isPhone, isAllSelected]);

  const isGlobalMachineList = currentMode === MODE.LIST && isPhone;

  const {
    server: { offset = 0 },
  } = time;

  const fetchData = async () => {
    await getMachines();
    await getTimeSetup();

    await getConnectorInfo();
  };

  useFocusEffect(
    useCallback(() => {
      setIsFocused(true);
      return () => setIsFocused(false);
    }, [])
  );

  useFocusEffect(
    useCallback(() => {
      if (!shouldWorkWithDateTime) {
        setTimePeriod(null);
        setSelectedChartDate(null);
        setSelectedChartDateEnd(null);
      }
    }, [shouldWorkWithDateTime])
  );

  useFocusEffect(
    useCallback(() => {
      setInitialLoading(initialLoadingGlobal);
    }, [initialLoadingGlobal])
  );

  useFocusEffect(
    useCallback(() => {
      setInitialEventDate(route?.params?.eventsDate ? localTime(route?.params?.eventsDate).format() : null);
    }, [route?.params?.eventsDate])
  );

  useEffect(() => {
    if (isPhone && selectedItem && !Object.keys(selectedItem)?.length) {
      setSelectedMachine(ALL_MACHINES_OBJECT);
    }
  }, []);

  useEffect(() => {
    const startPolling = (): void => {
      if (interval) return;
      interval = window.setInterval(() => {
        updateMachineInfo();
      }, 1000 * 5);
    };

    const stopPolling = (): void => {
      if (interval) {
        window.clearInterval(interval);
        interval = null;
      }
    };

    const handleVisibilityChange = (isVisible: boolean): void => {
      if (isVisible) {
        fetchData().catch((e) => console.error('[MachinesProvider] visibility fetchData failed', e));
        startPolling();
      } else {
        stopPolling();
      }
    };

    const unsubscribeVisibility = appVisibilityService.subscribe(handleVisibilityChange);

    if (!isWeb) {
      IdleTimerManager.setIdleTimerDisabled(true);
    }

    startPolling();

    const shouldLoadData = (firstRenderMachinesProvider || !machinesLoaded) && isMachinesScreen;
    if (shouldLoadData) {
      (async () => {
        try {
          await getMachines(false, id);
        } catch (e) {
          console.error('[MachinesProvider] initial getMachines failed', e);
        } finally {
          setInitialLoading(false);
          initialLoadingGlobal = false;
        }
      })();
    } else {
      setInitialLoading(false);
      initialLoadingGlobal = false;
    }

    setFirstRenderMachinesProvider(false);

    return () => {
      stopPolling();
      unsubscribeVisibility();

      if (!isWeb) {
        IdleTimerManager.setIdleTimerDisabled(false);
      }
    };
  }, []);

  const listWidth = s(LIST_WIDTH);

  const contextProps: MachinesContextProps = {
    isGlobalMachineList,
    lg,
    onBack,
  };

  const listProps = {
    data: machines,
    machinesLoaded,
    machines: machinesStorage,
    setSelectedMachine,
    selectedItem,
    timeOffset: offset,
    listWidth,
    generateImageURL,
    machineStatistics,
    machineListOEE,
  };

  const detailsWrapperProps = { withDefaultComponent, globalScroll, withoutHeader };

  const renderComponent = () => {
    const ListComponent = getList?.(listProps);

    switch (currentMode) {
      case MODE.DETAILS: {
        return <MachinesScreenContentWrapper {...detailsWrapperProps}>{children}</MachinesScreenContentWrapper>;
      }
      case MODE.LIST: {
        return ListComponent;
      }
      case MODE.FULL: {
        return [
          <React.Fragment key="ListComponent">{ListComponent}</React.Fragment>,
          <View key="DetailsComponent" style={{ alignItems: 'center', flex: 1 }}>
            <MachinesScreenContentWrapper {...detailsWrapperProps}>{children}</MachinesScreenContentWrapper>
          </View>,
        ];
      }
    }
  };

  return (
    <MachinesContext.Provider value={contextProps}>
      <View
        key="machineDetailsBlock"
        style={[
          {
            width: '100%',
            height: panelViewMode ? fullHeight : '100%',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
          },
          (isWeb || isMachinesScreen) && { backgroundColor: C('Background/Light/Detail') },
        ]}
      >
        <View style={{ width: '100%', flex: 1 }}>
          <View style={{ flexDirection: 'row', flex: 1 }}>{renderComponent()}</View>
        </View>
        <MissingPIModal key="MissingPIModalGlobal" />
        {!isPhone && isFocused && <MachineActionsModal panelViewMode={panelViewMode} />}
        {isMachinesSelectModalOpen && <MachinesSelectModal {...{ C, machines, selectedMachine: selectedItem, setSelectedMachine, panelViewMode }} />}
      </View>
    </MachinesContext.Provider>
  );
};

const mapStateToProps = ({
  machines,
  time,
  statistics: { machineListOEE },
  machinesV2: { isMachinesSelectModalOpen, actionsModalOpen, firstRenderMachinesProvider },
}) => ({
  machines,
  time,
  machineListOEE,
  isMachinesSelectModalOpen,
  actionsModalOpen,
  firstRenderMachinesProvider,
});

export default connect(mapStateToProps, {
  getMachines,
  setSelectedMachine,
  setSelectedTime,
  updateMachineInfo,
  generateImageURL,
  getTimeSetup,
  getConnectorInfo,
  setGlobalSelectedLevel,
  setInitialLoading,
  setInitialEventDate,
  setFirstRenderMachinesProvider,
  setSelectedChartDate,
  setSelectedChartDateEnd,
  setTimePeriod,
})(ColorWrapper(MachinesProvider));
